import { INTERVAL_CHECKING_WALLET } from "constants/network";
import { USER_CHECK_STATUS_ENUM, RISK_LEVEL_ENUM, UserCheckWalletStatus, WalletRiskLevel } from "interfaces";
import { useEffect, useState, useRef } from "react";
import { NormalizedError, getResultByCheckId, errorHandler } from "services";
import { createUserWalletCheck } from "services";
import { parseErrorMessage } from "utils/errors";
import { useWalletCheckEvents } from "./useWebSocketNotifications";

export const createAmlCheck = async (address: string): Promise<string | null> => {
  try {
    const response = await createUserWalletCheck({ walletAddress: address });
    return response;
  } catch (error: any) {
    errorHandler(error);
    return null;
  }
};

/**
 * Хук для проверки риска кошелька(если нет подключения по ws, то используется опросник)
 *
 * @param address - адрес кошелька для проверки
 * @param checkRisk - флаг для запуска проверки
 */
export const useUserCheckWallet = (address: string, checkRisk: boolean) => {
  const { subscribeToWalletCheck, isConnected } = useWalletCheckEvents();

  const [amlId, setAmlId] = useState<string | null>(null);
  const [checkId, setCheckId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [risk, setRisk] = useState<WalletRiskLevel>(RISK_LEVEL_ENUM.UNDEFINED);
  const [currentStatus, setCurrentStatus] = useState<UserCheckWalletStatus | null>(null);
  const [fallbackToPolling, setFallbackToPolling] = useState(false);
  const timeoutReached = useRef(false);

  useEffect(() => {
    const fetchAndSetId = async () => {
      setRisk(RISK_LEVEL_ENUM.UNDEFINED);
      setCurrentStatus(USER_CHECK_STATUS_ENUM.INITIAL);
      setErrorMessage(null);
      timeoutReached.current = false;
      setFallbackToPolling(false);

      const fetchedId = await createAmlCheck(address);
      setAmlId(fetchedId);
    };

    if (checkRisk && address) {
      fetchAndSetId();
    } else {
      setAmlId(null);
      setCurrentStatus(null);
      setErrorMessage(null);
      timeoutReached.current = false;
      setFallbackToPolling(false);
    }
  }, [address, checkRisk]);

  useEffect(() => {
    if (!checkRisk || !amlId) {
      return;
    }

    if (isConnected) {
      const unsubscribe = subscribeToWalletCheck(amlId, (event) => {
        setCurrentStatus(event.status);

        if (event.status === USER_CHECK_STATUS_ENUM.COMPLETED) {
          setRisk(event.riskLevel);
          setCheckId(event.id);
        }
      });

      return unsubscribe;
    } else {
      setFallbackToPolling(true);
    }
  }, [checkRisk, amlId, isConnected, subscribeToWalletCheck]);

  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;

    const fetchStatus = async () => {
      if (amlId && !timeoutReached.current) {
        try {
          const response = await getResultByCheckId({ checkId: amlId });

          setCurrentStatus(response.status);

          if (response.status === USER_CHECK_STATUS_ENUM.COMPLETED) {
            if (intervalId) clearInterval(intervalId);
            if (response.riskLevel) {
              setRisk(response.riskLevel);
            }
            if (response.checkId) {
              setCheckId(response.checkId);
            }
          }
        } catch (error) {
          console.error(error);
          const errorMsg = parseErrorMessage(error as NormalizedError);
          setErrorMessage(errorMsg);
        }
      }
    };

    if (checkRisk && amlId && fallbackToPolling && !timeoutReached.current) {
      fetchStatus();
      intervalId = setInterval(fetchStatus, INTERVAL_CHECKING_WALLET);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [amlId, checkRisk, fallbackToPolling]);

  return {
    riskLevel: risk,
    amlId: amlId,
    checkId: checkId,
    errorMessage,
    status: currentStatus,
  };
};
